import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Input, Popconfirm, Popover, Space, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import moment from 'jalali-moment';
import { useRef } from 'react';
import { MdScreenshotMonitor } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCalendar from '../hooks/useCalendar';
import { fontClass } from 'i18n';
import { t } from 'i18next';

const getTooltipContent = (type, conditions, majority) => {
  if (!Array.isArray(conditions)) return null;

  if (type === 'DeviceName') {
    const uniqueDevices = conditions.filter(
      (c, idx, self) =>
        idx === self.findIndex((d) => d.equipmentTitle === c.equipmentTitle),
    );
    return uniqueDevices.map((c, idx) => (
      <div key={idx}>{c.equipmentTitle}</div>
    ));
  }

  if (type === 'SignalName') {
    const uniqueSignals = conditions.filter(
      (c, idx, self) =>
        idx ===
        self.findIndex(
          (d) => d.parameterDescription === c.parameterDescription,
        ),
    );
    return uniqueSignals.map((c, idx) => (
      <div key={idx}>{c.parameterDescription}</div>
    ));
  }

  if (type === 'Majority') {
    return <div>{majority}</div>;
  }

  return null;
};

const renderWithTooltips = (text, conditions, majority) => {
  if (!text) return '';
  const parts = text.split(
    /(\{\{DeviceName\}\}|\{\{SignalName\}\}|\{\{Majority\}\})/g,
  );

  return parts.map((part, index) => {
    if (part === '{{DeviceName}}') {
      return (
        <Tooltip
          key={index}
          title={getTooltipContent('DeviceName', conditions)}
        >
          <span style={{ color: '#1890ff', cursor: 'pointer' }}>
            DeviceName
          </span>
        </Tooltip>
      );
    }
    if (part === '{{SignalName}}') {
      return (
        <Tooltip
          key={index}
          title={getTooltipContent('SignalName', conditions)}
        >
          <span style={{ color: '#52c41a', cursor: 'pointer' }}>
            SignalName
          </span>
        </Tooltip>
      );
    }

    if (part === '{{Majority}}') {
      return (
        <Tooltip
          key={index}
          title={getTooltipContent('Majority', conditions, majority)}
        >
          <span style={{ color: '#dd1818', cursor: 'pointer' }}>Majority</span>
        </Tooltip>
      );
    }
    return part;
  });
};

export const AlarmsHeader = (state, setState, calendar) => {
  return [
    {
      title: <span className={fontClass}>{t('category')}</span>,
      dataIndex: 'category',
      align: 'center',
      render: (_, elm) => {
        const colors = {
          MAJOR: { bg: '#ffe4e4', color: '#f90000', border: '#ff4a4a' },
          MINOR: { bg: '#ffffd3', color: '#ff6c00', border: '#ffde00' },
          EVENT: { bg: '#ebf5ff', color: '#0070fb', border: '#60a7ff' },
        };
        const style = colors[elm.category] || {};
        return (
          <Tag
            style={{
              backgroundColor: style.bg,
              color: style.color,
              border: `1px solid ${style.border}`,
            }}
          >
            {elm.category}
          </Tag>
        );
      },
      width: 125,
    },
    {
      title: <span className={fontClass}>{t('name')}</span>,
      dataIndex: 'title',
      align: 'center',
      fixed: 'left',
      width: 200,
      render: (_, elm) => (
        <div style={{ direction: 'rtl', textAlign: 'center' }}>
          {renderWithTooltips(_, elm.conditions)}
        </div>
      ),
    },
    {
      title: <span className={fontClass}>{t('description')}</span>,
      dataIndex: 'description',
      align: 'center',
      render: (_, elm) => {
        const truncatedText = _.length > 80 ? _.slice(0, 80) + '...' : _;
        return (
          <div style={{ direction: 'rtl', textAlign: 'center' }}>
            {renderWithTooltips(truncatedText, elm.conditions, elm.category)}
          </div>
        );
      },
      width: 250,
    },
    {
      title: <span className={fontClass}>{t('date')}</span>,
      dataIndex: 'CreationDate',
      align: 'center',
      render: (_, elm) => {
        const formattedDate = calendar
          ? moment(elm.creationDate).locale('fa').format('YYYY/MM/DD')
          : moment(elm.creationDate).format('YYYY/MM/DD');
        const formattedTime = moment(elm.creationDate).format('HH:mm:ss');
        return (
          <div>
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
      width: 150,
    },
    // {
    //   title: 'User',
    //   dataIndex: 'user',
    //   align: 'center',
    //   render: (_, elm) => <span>{elm.user?.username}</span>,
    //   width: 150,
    // },
    {
      title: <span className={fontClass}>{t('action')}</span>,
      dataIndex: 'actions',
      align: 'center',
      render: (_, elm) => {
        return (
          <div className="text-right flex justify-center gap-3">
            <UnorderedListOutlined
              className="text-sm text-[#fab209] bg-[#ffc0122c] py-2 px-3 rounded-lg"
              onClick={() => {
                setState({
                  ...state,
                  ...elm,
                  isDetail: true,
                  ...(elm.logic === 'AND'
                    ? { isDetailsModalVisible: true }
                    : { isGroupModalVisible: true }),
                });
              }}
            />
            <EditOutlined
              onClick={() => {
                setState({
                  ...state,
                  ...elm,
                  isDetail: false,
                  ...(elm.logic === 'AND'
                    ? { isModalVisible: true }
                    : { isGroupModalVisible: true }),
                });
              }}
              className="text-sm text-[#6E6FF7] bg-[#6E6FF71A] py-2 px-3 rounded-lg"
            />

            <DeleteOutlined
              onClick={() =>
                setState({
                  ...state,
                  showDeleteModal: true,
                  id: elm.ID,
                  ...elm,
                })
              }
              className="text-sm text-[#FF0000] bg-[#FF00001A] py-2 px-3 rounded-lg"
            />
          </div>
        );
      },
      width: 150,
    },
  ];
};
export const usersHeader = (state, setState, handleBlockUser) => {
  const userInfo = JSON.parse(localStorage.getItem('user_info'));
  return [
    {
      title: 'Username',
      dataIndex: 'username',
      align: 'center',
      // fixed: 'left',
      width: 100,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      align: 'center',
      width: 150,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      align: 'center',
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      align: 'center',
      width: 250,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      align: 'center',
      width: 150,
    },
    // {
    //   title: "Block",
    //   dataIndex: "block",
    //   align: "center",
    //   render: (_, elm) => (
    //     <>
    //       {elm.role !== "ADMIN" && (
    //         <Checkbox
    //           checked={elm.block}
    //           onChange={() => handleBlockUser(elm)}
    //         />
    //       )}
    //     </>
    //   ),
    // },
    {
      title: 'Actions',
      dataIndex: 'actions',
      align: 'center',
      width: 150,

      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {elm.role !== 'ADMIN' ? (
            <Popconfirm
              placement="left"
              disabled={elm.role == 'ADMIN'}
              title="Do you want to block this user?"
              description={`Engaging in this activity will result in ${
                elm.block ? 'connection' : 'disconnection'
              } of user access.`}
              onConfirm={() => handleBlockUser(elm)}
              okText="Yes"
              cancelText="No"
            >
              <button
                className={classNames('border-0 w-20 rounded-lg', {
                  'cursor-not-allowed': elm.role == 'ADMIN',
                  'text-[#6E6FF7] bg-[#8a8ae730]': !elm.block,
                  'text-[#FF0000] bg-[#FF00001A]': elm.block,
                })}
              >
                {elm.block ? 'Unblock' : 'Block'}
              </button>
            </Popconfirm>
          ) : (
            <div className="w-20"></div>
          )}
          <Button
            icon={<EditOutlined />}
            className="border-0 w-10 text-[#6E6FF7] bg-[#8a8ae730]  rounded-lg"
            onClick={() => {
              setState({
                ...state,
                ...elm,
                isModalVisible: true,
              });
            }}
          />
          <Button
            icon={<DeleteOutlined />}
            disabled={elm.ID == userInfo.ID}
            onClick={() =>
              setState({
                ...state,
                showDeleteModal: true,
                id: elm.ID,
                ...elm,
              })
            }
            className="border-0 w-10 text-[#FF0000] bg-[#FF00001A]  rounded-lg"
          />
        </div>
      ),
    },
  ];
};

export const DeviceManagementHeader = (
  state,
  setState,
  handleDeviceResetProcess,
  loadingDeviceCodes,
  IS_ADMIN,
) => {
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const goToDeviceInformation = (elm) => {
    navigate('/device-information', { state: elm });
  };
  const buildFilterKey = (dataIndex) => {
    if (Array.isArray(dataIndex)) return `filter_${dataIndex.join('_')}`;
    return `filter_${dataIndex}`;
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    const key = buildFilterKey(dataIndex);
    const next = new URLSearchParams(searchParams);
    if (selectedKeys && selectedKeys[0]) {
      next.set(key, selectedKeys[0]);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  };
  const handleReset = (clearFilters, confirm, dataIndex) => {
    if (clearFilters) clearFilters();
    confirm();
    const key = buildFilterKey(dataIndex);
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next);
  };

  const getColumnSearchProps = (dataIndex) => {
    const key = buildFilterKey(dataIndex);
    const paramValue = searchParams.get(key) ?? null;
    const filteredValue = paramValue ? [paramValue] : null;

    return {
      filteredValue,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${
              Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex
            }`}
            value={selectedKeys[0] ?? paramValue ?? ''}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8, display: 'block' }}
            className="rounded-lg border border-gray-200 p-0.5"
          />
          <Space>
            <Button
              className="bg-indigo-500 text-white rounded-md"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              className="rounded-md"
              onClick={() => handleReset(clearFilters, confirm, dataIndex)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) => {
        const val = Array.isArray(dataIndex)
          ? dataIndex.reduce((acc, k) => (acc ? acc[k] : undefined), record)
          : record[dataIndex];
        if (val === null || typeof val === 'undefined') return false;
        return val.toString().toLowerCase().includes(value.toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
      render: (text) => text,
    };
  };

  return [
    {
      title: 'Title',
      dataIndex: 'Title',
      align: 'center',
      ...getColumnSearchProps('Title'),
      fixed: 'left',
      width: 100,
    },
    {
      title: 'Count of Panel',
      dataIndex: 'Panels',
      align: 'center',
      render: (elm) => (
        <div className=" flex justify-center gap-3 ">
          {elm == null ? '--' : elm}
        </div>
      ),
      width: 150,
    },
    {
      title: 'URL',
      dataIndex: 'Url',
      align: 'center',
      ...getColumnSearchProps('Url'),
      width: 150,
    },
    {
      title: 'Phase',
      dataIndex: ['phase', 'Name'],
      // ...getColumnSearchProps(['phase', 'Name']),
      align: 'center',

      width: 100,
    },
    {
      title: 'FarCom ID',
      dataIndex: 'Serial',
      ...getColumnSearchProps('Serial'),
      align: 'center',
      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {!elm.Serial ? '__' : elm.Serial}
        </div>
      ),
      width: 150,
    },
    {
      title: 'Device Code',
      dataIndex: 'DeviceCode',
      ...getColumnSearchProps('DeviceCode'),
      align: 'center',
      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {!elm.DeviceCode ? '__' : elm.DeviceCode}
        </div>
      ),
      width: 150,
    },
    {
      title: 'Port',
      dataIndex: 'Port',
      ...getColumnSearchProps('Port'),
      align: 'center',
      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {!elm.Port ? '__' : elm.Port}
        </div>
      ),
      width: 150,
    },
    {
      title: 'Slave ID',
      dataIndex: 'SlaveID',
      ...getColumnSearchProps('SlaveID'),
      align: 'center',
      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {!elm.SlaveID ? '__' : elm.SlaveID}
        </div>
      ),
      width: 150,
    },
    {
      title: 'Sub Category',
      dataIndex: 'subCategory',
      ...getColumnSearchProps('subCategory'),
      align: 'center',
      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          {!elm.subCategory ? '__' : elm.subCategory}
        </div>
      ),
      width: 150,
    },

    {
      title: 'Action',
      align: 'center',
      render: (_, record) => {
        const isResetDeviceLoading = loadingDeviceCodes.includes(
          record.DeviceCode,
        );
        return (
          <Space>
            {IS_ADMIN && (
              <>
                <Tooltip placement="top" title="Edit">
                  <EditOutlined
                    onClick={() => {
                      setState({ ...state, ...record, isModalVisible: true });
                    }}
                    className="text-sm text-[#6E6FF7] bg-[#6E6FF71A] py-2 px-3 rounded-lg"
                  />
                </Tooltip>
                <Tooltip placement="top" title="Delete">
                  <DeleteOutlined
                    onClick={() => {
                      setState({
                        ...state,
                        ...record,
                        showDeleteModal: true,
                      });
                    }}
                    className="text-sm text-[#FF0000] bg-[#FF00001A] py-2 px-3 rounded-lg"
                  />
                </Tooltip>
              </>
            )}
            <Tooltip placement="top" title="Monitoring">
              <div
                className="text-base text-[#00b706] bg-[#00ff0840] py-2 px-3 rounded-lg cursor-pointer"
                onClick={() => {
                  // console.log(record)
                  goToDeviceInformation(record);
                }}
              >
                {/* <div className="text-base text-[#9e9e9e] bg-[#9e9e9e5c] py-2 px-3 rounded-lg"> */}
                <MdScreenshotMonitor />
              </div>
            </Tooltip>
            <Tooltip placement="top" title="Reset Device">
              <Button
                loading={isResetDeviceLoading}
                onClick={() => handleDeviceResetProcess(record.DeviceCode)}
                icon={<ReloadOutlined />}
                className="text-sm text-[#fab209] bg-[#ff91001a] rounded-lg border-none"
              />
            </Tooltip>
          </Space>
        );
      },
      width: 200,
    },
  ];
};
export const AlarmEventHeader = () => {
  const categoryMap = {
    '-1': 'DISCONNECT',
    402: 'EVENT STATUS',
    400: 'LOW POWER',
    401: 'SHUTDOWN',
  };
  const calendar = useCalendar();
  return [
    {
      title: 'Alarms Category',
      dataIndex: 'EventStatusNo',
      align: 'center',
      width: 125,
      // filters: Object.entries(categoryMap).map(([value, text]) => ({
      //   text,
      //   value,
      // })),
      // onFilter: (value, record) => {
      //   return value === record.EventStatusNo.toString();
      // },
      render: (_, elm) => categoryMap[elm.EventStatusNo] || '--',
    },
    {
      title: 'Title',
      dataIndex: 'equipment',
      align: 'center',
      fixed: 'left',
      width: 125,
    },
    {
      title: 'Status',
      dataIndex: 'currentStatus',
      align: 'center',
      width: 50,
      render: (_, elm) => elm.currentStatus || '--',
    },
    {
      title: 'Display',
      dataIndex: 'Display',
      align: 'center',
      render: (elm) =>
        elm == null ? (
          '--'
        ) : (
          <Popover content={elm}>
            {elm.length >= 30 ? `${elm?.substring(0, 25)}...` : elm}
          </Popover>
        ),
      width: 125,
    },
    {
      title: 'Description',
      dataIndex: 'Description',
      align: 'center',
      render: (elm) =>
        elm == null ? (
          '--'
        ) : (
          <Popover content={elm}>
            {elm.length >= 100 ? `${elm?.substring(0, 95)}...` : elm}
          </Popover>
        ),
      width: 220,
    },
    {
      title: 'Creation Date',
      dataIndex: 'CreationDate',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps('CreationDate'),
      render: (_, elm) => {
        const formattedDate = calendar
          ? moment(elm.CreationDate).locale('fa').format('YYYY/MM/DD')
          : elm.CreationDate?.slice(0, 10);
        const formattedTime = elm.CreationDate?.slice(11, 23);
        return (
          <div className="">
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
    },
    {
      title: 'Resolved Date',
      dataIndex: 'ResolvedDate',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps('ResolvedDate'),
      render: (_, elm) => {
        if (!elm.ResolvedDate) {
          return <div>ACTIVE</div>;
        }

        const formattedDate = calendar
          ? moment(elm.ResolvedDate).locale('fa').format('YYYY/MM/DD')
          : elm.ResolvedDate.slice(0, 10);

        const formattedTime = elm.ResolvedDate.slice(11, 23);

        return (
          <div>
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
    },
  ];
};
export const EventHistoryTableHeader = (getDeviceNameById) => {
  const toLocalMoment = (raw) => {
    if (!raw) return null;
    if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(raw) || /[+\-]\d{4}$/.test(raw)) {
      return moment.parseZone(raw).local();
    }
    return moment.utc(raw).local();
  };
  const calendar = useCalendar();
  return [
    {
      title: (<span className={fontClass}>{t('alarmsCategory')}</span>),
      dataIndex: 'EventStatusNo',
      align: 'center',
      width: 180,
      render: (_, elm) => {
        const colors = {
          MAJOR: { bg: '#ffe4e4', color: '#f90000', border: '#ff4a4a' },
          MINOR: { bg: '#ffffd3', color: '#ff6c00', border: '#ffde00' },
          EVENT: { bg: '#ebf5ff', color: '#0070fb', border: '#60a7ff' },
        };
        const style = colors[elm.event?.category] || {};
        return (
          <Tag
            style={{
              backgroundColor: style.bg,
              color: style.color,
              border: `1px solid ${style.border}`,
            }}
          >
            {elm.event?.category}
          </Tag>
        );
      },
    },
    // {
    //   title: 'Title',
    //   dataIndex: 'title',
    //   align: 'center',
    //   fixed: 'left',
    //   width: 125,
    //   render: (elm) => <div dir="auto">{elm}</div>,
    // },
    {
      title: (<span className={fontClass}>{t('deviceName')}</span>),
      dataIndex: 'equipmentId',
      align: 'center',
      fixed: 'left',
      width: 125,
      render: (elm) => <div dir="auto">{getDeviceNameById(elm)}</div>,
    },
    {
      title: (<span className={fontClass}>{t('type')}</span>),
      dataIndex: 'type',
      align: 'center',
      width: 100,
      render: (elm) => <span className={fontClass}>{t(elm)}</span>
    },
    {
      title: (<span className={fontClass}>{t('status')}</span>),
      dataIndex: 'currentValue',
      align: 'center',
      width: 100,
      render: (_, elm) => (elm.type === 'STATUS' ? elm.currentValue : '--'),
    },
    {
      title: (<span className={fontClass}>{t('display')}</span>),
      dataIndex: 'display',
      align: 'center',
      render: (elm) =>
        elm == null ? (
          '--'
        ) : (
          <div dir="auto">
            <Popover content={<div dir="auto">{elm}</div>}>
              {elm.length >= 30 ? `${elm?.substring(0, 25)}...` : elm}
            </Popover>
          </div>
        ),
      width: 125,
    },
    {
      title: (<span className={fontClass}>{t('description')}</span>),
      dataIndex: 'description',
      align: 'center',
      render: (elm) =>
        elm == null ? (
          '--'
        ) : (
          <div dir="auto">
            <Popover content={<div dir="auto">{elm}</div>}>
              {elm.length >= 100 ? `${elm?.substring(0, 95)}...` : elm}
            </Popover>
          </div>
        ),
      width: 220,
    },
    {
      title: (<span className={fontClass}>{t('creationDate')}</span>),
      dataIndex: 'occurrenceEventDate',
      align: 'center',
      width: 130,
      render: (_, elm) => {
        const raw = elm.occurrenceEventDate;
        if (!raw) return <div>--</div>;
        const m = toLocalMoment(raw);
        if (!m || !m.isValid()) return <div>--</div>;
        const formattedDate = calendar
          ? moment(m).locale('fa').format('YYYY/MM/DD')
          : m.format('YYYY/MM/DD');
        const formattedTime = m.format('HH:mm:ss');
        return (
          <div>
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
    },
    {
      title: (<span className={fontClass}>{t('resolvedDate')}</span>),
      dataIndex: 'EventResolutionDate',
      align: 'center',
      width: 130,
      render: (_, elm) => {
        if (!elm.EventResolutionDate) {
          return <div className={fontClass}>{t('active')}</div>;
        }
        const raw = elm.EventResolutionDate;
        const m = toLocalMoment(raw);
        if (!m || !m.isValid()) return <div>--</div>;
        const formattedDate = calendar
          ? moment(m).locale('fa').format('YYYY/MM/DD')
          : m.format('YYYY/MM/DD');
        const formattedTime = m.format('HH:mm:ss');
        return (
          <div>
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
    },
  ];
};
export const EmergencyAlarmHistoryHeader = () => {
  const calendar = useCalendar();
  return [
    {
      title: 'Category',
      dataIndex: ['alarm', 'category'],
      align: 'center',
      fixed: 'left',
      width: 125,
      render: (_, elm) => {
        return (
          <Tag
            style={{
              backgroundColor:
                elm?.alarm?.category === 'MAJOR'
                  ? '#ffe4e4'
                  : elm?.alarm?.category === 'MINOR'
                  ? '#ffffd3'
                  : '#ebf5ff',
              color:
                elm?.alarm?.category === 'MAJOR'
                  ? '#f90000'
                  : elm?.alarm?.category === 'MINOR'
                  ? '#ff6c00'
                  : '#0070fb',
              border: `1px solid ${
                elm?.alarm?.category === 'MAJOR'
                  ? '#ff4a4a'
                  : elm?.alarm?.category === 'MINOR'
                  ? '#ffde00'
                  : '#60a7ff'
              }`,
            }}
          >
            {_}
          </Tag>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: ['alarm', 'Title'],
      align: 'center',
      fixed: 'left',
      width: 125,
      // render: (elm) =>
      //   elm == null ? (
      //     '--'
      //   ) : (
      //     <Popover content={elm}>
      //       {elm.length >= 100
      //         ? `${elm.substring(0, 95)}...`
      //         : elm}
      //     </Popover>
      //   ),
    },
    {
      title: 'Description',
      dataIndex: ['alarm', 'Description'],
      align: 'center',
      render: (elm) =>
        elm == null ? (
          '--'
        ) : (
          <Popover
            content={elm}
            overlayStyle={{ width: '500px', direction: 'rtl' }}
          >
            <p style={{ direction: 'rtl' }} className="truncate max-w-80">
              {elm.length >= 100 ? `${elm?.substring(0, 95)}...` : elm}
            </p>
          </Popover>
        ),
      width: 220,
    },
    {
      title: 'username',
      dataIndex: ['user', 'username'],
      align: 'center',
      render: (elm) => (elm == null ? 'Automatic' : elm),
      width: 220,
    },

    {
      title: 'OccurrenceDate',
      dataIndex: 'OccurrenceDate',
      align: 'center',
      render: (_, elm) => {
        const formattedDate = calendar
          ? moment(elm.OccurrenceDate).locale('fa').format('YYYY/MM/DD')
          : elm.OccurrenceDate?.slice(0, 10);
        const formattedTime = elm.OccurrenceDate?.slice(11, 23);
        return (
          <div className="">
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
      width: 150,
    },
    {
      title: 'ResolvedDate',
      dataIndex: 'ResolvedDate',
      align: 'center',
      render: (_, elm) => {
        if (!elm.ResolvedDate) return 'Not Resolve';

        const formattedDate = calendar
          ? moment(elm.ResolvedDate).locale('fa').format('YYYY/MM/DD')
          : elm.ResolvedDate.slice(0, 10);
        const formattedTime = elm.ResolvedDate.slice(11, 23);

        return (
          <div>
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },

      width: 150,
    },
  ];
};

export const LogsHeader = () => {
  const calendar = useCalendar();
  return [
    {
      title: 'Api Name ',
      dataIndex: 'ApiName_EN',
      align: 'center',
      // ...getColumnSearchProps("ApiName_EN"),
      render: (_, elm) => (
        <div className="">
          <Tooltip
            title={
              <div className="flex flex-col gap-2">
                <span className="">{`Api Name FA  :  ${elm.ApiName_FA}`}</span>
              </div>
            }
            color={'#7878b1'}
            key={elm.ID}
          >
            <div>{elm.ApiName_EN}</div>
          </Tooltip>
        </div>
      ),
      fixed: 'left',
      width: 125,
    },
    {
      title: 'Method',
      dataIndex: 'Method',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps("Method"),
    },

    {
      title: 'Username',
      dataIndex: 'User',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps("Username"),
      render: (_, elm) => {
        return (
          <div className="">
            <Tooltip
              title={
                <div className="flex flex-col gap-2">
                  {/* <span className="">{`User-ID  :  ${elm.User_ID}`}</span> */}
                  <span className="">{`IP  :  ${elm.IP}`}</span>
                </div>
              }
              color={'#7878b1'}
              key={elm.ID}
            >
              <div>{elm.User?.username == null ? '--' : elm.User.username}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Browser',
      dataIndex: 'Browser',
      align: 'center',
      width: 150,
      render: (_, elm) => (
        <div className="">
          <Tooltip
            title={
              <div className="flex flex-col gap-2">
                <span className=""> {elm.Browser}</span>
              </div>
            }
            color={'#7878b1'}
            key={elm.ID}
          >
            <div>
              {elm.Browser.slice(0, 12)}
              {elm.Browser.length > 11 ? '...' : ''}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Status Code',
      dataIndex: 'StatusCode',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps("StatusCode"),
    },
    {
      title: 'Message',
      dataIndex: 'Message',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps("Message"),
    },
    {
      title: 'Duration Time',
      dataIndex: 'DurationTime',
      align: 'center',
      width: 150,
      // ...getColumnSearchProps("DurationTime"),
    },

    {
      title: 'Creation Date',
      dataIndex: 'CreationDate',
      align: 'center',
      width: 150,
      // ...getColumnSearchPropsCalender("CreationDate"),
      // sorter: (a, b) => new Date(a.CreationDate) - new Date(b.CreationDate),
      sortDirections: ['descend', 'ascend'],
      render: (_, elm) => {
        const formattedDate = calendar
          ? moment(elm.CreationDate).locale('fa').format('YYYY/MM/DD')
          : elm.CreationDate?.slice(0, 10);
        const formattedTime = elm.CreationDate?.slice(11, 16);
        return (
          <div className="">
            <div>{formattedDate}</div>
            <div>({formattedTime})</div>
          </div>
        );
      },
    },
    {
      title: 'Request body',
      dataIndex: 'Request_body',
      align: 'center',
      width: 150,
      render: (_, elm) => (
        <div className="">
          <div>
            {elm.Request_body == null ? (
              '--'
            ) : (
              <Popover
                content={
                  <div className="text-white">
                    {' '}
                    {JSON.stringify(elm?.Request_body)}
                  </div>
                }
                placement="left"
                trigger="click"
                color={'#7878b1'}
              >
                {/* <Button>Click me</Button> */}
                <EyeOutlined
                  onClick={() => {}}
                  className="text-sm text-[#6E6FF7] bg-[#8a8ae730] py-2 px-3 rounded-lg"
                />
              </Popover>
            )}
          </div>
        </div>
      ),
    },
  ];
};

export const PhasesHeader = (state, setState) => {
  function extractDateTime(dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const date = dateTime.toLocaleDateString();
    const time = dateTime.toLocaleTimeString();
    return `${date} ${time}`;
  }
  return [
    {
      title: 'Name',
      dataIndex: 'Name',
      align: 'center',
      width: 150,
    },
    {
      title: (
        <>
          Capacity (<span className="text-xs">kW</span>)
        </>
      ),
      dataIndex: 'Capacity',
      align: 'center',
      width: 150,
    },
    {
      title: (
        <>
          Panel Area{' '}
          <span className="text-xs">
            m<sup>2</sup>
          </span>
        </>
      ),
      dataIndex: 'PerformanceRatio',
      align: 'center',
      width: 150,
      render: (performanceRatio) => performanceRatio?.panelArea,
    },
    {
      title: 'Panel Numbers',
      dataIndex: 'PerformanceRatio',
      align: 'center',
      width: 150,
      render: (performanceRatio) => performanceRatio?.panelNumbers,
    },
    {
      title: 'User',
      dataIndex: 'user',
      align: 'center',
      render: (_) => `${_?.username ?? '---'}`,
      width: 150,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      align: 'center',
      width: 150,

      render: (_, elm) => (
        <div className=" flex justify-center gap-3 ">
          <Button
            icon={<EditOutlined />}
            className="border-0 w-10 text-[#6E6FF7] bg-[#8a8ae730]  rounded-lg"
            onClick={() => {
              setState({
                ...state,
                ...elm,
                isModalVisible: true,
              });
            }}
          />
          <Button
            icon={<DeleteOutlined />}
            // disabled={elm.ID == userInfo.ID}
            onClick={() =>
              setState({
                ...state,
                showDeleteModal: true,
                id: elm.ID,
                ...elm,
              })
            }
            className="border-0 w-10 text-[#FF0000] bg-[#FF00001A]  rounded-lg"
          />
        </div>
      ),
    },
  ];
};
